import type { PresetKey } from "@/components/v2/DatePicker/constants";
import type { KpiNumberData } from "@/components/v2/KpiNumbers";
import {
  comparisonKeys,
  getBottomTextView,
  getKpiValue,
} from "@/components/v2/KpiNumbers/utils";

import type { GetCustomersResponse } from "server/dwhApi/customers/models.js";
import type { GetTerminalResponse } from "server/dwhApi/terminals/models.js";

import { getContaminationRate } from "../../utils";

type Props = {
  currentData?: GetTerminalResponse;
  comparisonData?: GetTerminalResponse;
  datePreset?: PresetKey;
  customersData?: GetCustomersResponse;
  comparisonCustomersData?: GetCustomersResponse;
  presetHasComparison: boolean;
  t: (key: string) => string;
};

export const getKpiNumbersData = ({
  currentData,
  comparisonData,
  datePreset,
  customersData,
  comparisonCustomersData,
  presetHasComparison,
  t,
}: Props): KpiNumberData[] => {
  if (!currentData) {
    return [];
  }

  const hasNoComparisonData =
    !presetHasComparison || !comparisonData || !currentData;

  const comparisonKey = t(comparisonKeys[datePreset || ""]);

  return [
    {
      id: "contaminationRate",
      title: t("contaminationPage:kpiContaminationRate"),
      getValue: () => getKpiValue(getContaminationRate(currentData.terminal)),
      valueSuffix: "%",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: getContaminationRate(currentData.terminal),
          comparisonValue: getContaminationRate(comparisonData?.terminal!),
          comparisonKey,
          higherBetter: false,
          differenceSuffix: t("common:percentagePoints"),
        }),
    },
    {
      id: "contaminatedWeight",
      title: t("contaminationPage:kpiContaminatedWeight"),
      getValue: () =>
        getKpiValue(
          currentData?.terminal.totalContaminatedWasteWeight.quantity,
        ),
      valueSuffix: "kg",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            currentData?.terminal.totalContaminatedWasteWeight.quantity || 0,
          comparisonValue:
            comparisonData?.terminal.totalContaminatedWasteWeight.quantity || 0,
          comparisonKey,
          differenceSuffix: " kg",
        }),
    },
    {
      id: "nrContaminations",
      title: t("contaminationPage:kpiFlaggedLoads"),
      getValue: () =>
        getKpiValue(currentData?.terminal.totalContaminatedOccurrences),
      valueSuffix: `/${currentData.terminal.totalOccurrences}`,
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: currentData?.terminal.totalContaminatedOccurrences || 0,
          comparisonValue:
            comparisonData?.terminal.totalContaminatedOccurrences || 0,
          comparisonKey,
          differenceSuffix: "",
          comparePercentages: false,
        }),
    },
    {
      id: "tenantsAffected",
      title: t("contaminationPage:kpiTenantsAffected"),
      getValue: () =>
        customersData?.customers.filter((c) => !!c.totalContaminatedOccurrences)
          .length || 0,
      valueSuffix: `/${currentData.terminal.totalNonDeletedCustomersAcrossTerminal}`,
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            customersData?.customers.filter(
              (c) => !!c.totalContaminatedOccurrences,
            ).length || 0,
          comparisonValue:
            comparisonCustomersData?.customers.filter(
              (c) => !!c.totalContaminatedOccurrences,
            ).length || 0,
          comparisonKey,
          differenceSuffix: "",
        }),
    },
  ];
};
